// frontend/src/components/MfaSettings.jsx
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import * as authApi from "../api/auth";
import Spinner from "./Spinner";

/**
 * MfaSettings — manage two-factor authentication from account settings.
 *
 * States:
 *   - loading          : fetching status
 *   - "disabled"       : MFA off → "Enable 2FA" button
 *   - "setup"          : QR shown, awaiting the first TOTP code
 *   - "backup"         : MFA just enabled → show one-time backup codes
 *   - "enabled"        : MFA on → show remaining backup codes + disable form
 */
function MfaSettings() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // { mfa_enabled, backup_codes_remaining }
  const [mode, setMode] = useState("idle"); // idle | setup | backup | disabling
  const [setupData, setSetupData] = useState(null); // { totp_uri, qr_code_image_base64 }
  const [backupCodes, setBackupCodes] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      setStatus(await authApi.mfaStatus());
    } catch {
      toast.error("Could not load your 2FA status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      setSetupData(await authApi.mfaSetup());
      setMode("setup");
      setCode("");
    } catch {
      toast.error("Could not start 2FA setup. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { backup_codes: codes } = await authApi.mfaConfirm(code.trim());
      setBackupCodes(codes);
      setMode("backup");
      setCode("");
      await refresh();
      toast.success("Two-factor authentication is on.");
    } catch (err) {
      const s = err?.response?.status;
      toast.error(
        s === 401 ? "That code didn't work. Try again." : "Could not enable 2FA.",
      );
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await authApi.mfaDisable(code.trim());
      setMode("idle");
      setCode("");
      await refresh();
      toast.success("Two-factor authentication is off.");
    } catch (err) {
      const s = err?.response?.status;
      toast.error(
        s === 401 ? "That code didn't work. Try again." : "Could not disable 2FA.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Two-factor authentication
        </h2>
        <p className="text-sm text-gray-500">
          Add a second step to your sign-in using an authenticator app.
        </p>
      </div>

      {/* Backup codes shown once, right after enabling. */}
      {mode === "backup" && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-900 mb-2">
            Save these backup codes
          </p>
          <p className="text-xs text-amber-800 mb-3">
            Each code works once if you lose your authenticator. They won&apos;t be
            shown again.
          </p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm text-gray-800">
            {backupCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setMode("idle")}
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            I have saved these
          </button>
        </div>
      )}

      {/* Enabled view (status + disable). */}
      {mode === "idle" && status?.mfa_enabled && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-green-700">
            <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
            2FA is on · {status.backup_codes_remaining} backup codes remaining
          </div>
          <form onSubmit={disable} className="space-y-2">
            <label htmlFor="mfa-disable-code" className="block text-sm text-gray-700">
              Enter a current code to turn it off
            </label>
            <div className="flex gap-2">
              <input
                id="mfa-disable-code"
                type="text"
                autoComplete="one-time-code"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={busy}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                Disable 2FA
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Disabled view (start setup). */}
      {mode === "idle" && !status?.mfa_enabled && (
        <button
          type="button"
          onClick={startSetup}
          disabled={busy}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          Enable 2FA
        </button>
      )}

      {/* Setup view (QR + confirm code). */}
      {mode === "setup" && setupData && (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Scan this QR code with your authenticator app, then enter the 6-digit
            code it shows.
          </p>
          <img
            src={`data:image/png;base64,${setupData.qr_code_image_base64}`}
            alt="Authenticator QR code"
            className="h-44 w-44 border border-gray-200 rounded-lg"
          />
          <form onSubmit={confirmSetup} className="space-y-2">
            <div className="flex gap-2">
              <input
                id="mfa-confirm-code"
                type="text"
                autoComplete="one-time-code"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={busy}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {busy ? "Verifying…" : "Confirm"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default MfaSettings;
